import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';
import { ANALYTICS_MODULE_NAMES } from 'src/analytics/analytics-enums';

export interface TRoute {
  title: string;
  fullPath?: string;
  pattern: {
    path: string;
    exact?: boolean;
  };
  analyticsModuleName?: string;
  nested?: Record<string, TRoute>;
}

export const routes = Object.freeze({
  root: {
    title: 'Root',
    pattern: {
      path: '/',
    },
  },
  companies: {
    title: 'Companies',
    fullPath: '/',
    pattern: {
      path: '/',
    },
  },
  hierarchy: {
    title: 'Hierarchy',
    fullPath: '/company/:company/project/:project?',
    pattern: {
      path: '/company/:company/project/:project?',
    },
    analyticsModuleName: ANALYTICS_MODULE_NAMES.ESTIMATES, // TODO: check this with Erlend
  },
  project: {
    title: 'Project',
    fullPath: '/company/:company/project/:project/*',
    pattern: {
      path: 'company/:company/project/:project/*',
    },
  },
  overview: {
    title: 'Overview',
    fullPath: '/company/:company/project/:project/overview',
    pattern: {
      path: 'overview',
    },
    analyticsModuleName: ANALYTICS_MODULE_NAMES.OVERVIEW,
  },
  wellConfiguration: {
    title: 'Well Details',
    fullPath: '/company/:company/project/:project/well-details',
    pattern: {
      path: 'well-details',
    },
    analyticsModuleName: ANALYTICS_MODULE_NAMES.WELL_DETAILS,
  },
  activityModel: {
    title: 'Activity Model',
    fullPath: '/company/:company/project/:project/activity-model',
    pattern: {
      path: 'activity-model',
    },
    analyticsModuleName: ANALYTICS_MODULE_NAMES.ACTIVITY_MODEL,
  },
  risks: {
    title: 'Project Modifiers',
    fullPath: '/company/:company/project/:project/risks',
    pattern: {
      path: 'risks',
    },
    analyticsModuleName: ANALYTICS_MODULE_NAMES.PROJECT_MODIFIERS,
  },
  costSetup: {
    title: 'Cost Setup',
    fullPath: '/company/:company/project/:project/cost-setup',
    pattern: {
      path: 'cost-setup',
    },
    analyticsModuleName: ANALYTICS_MODULE_NAMES.COST_SETUP,
  },
  resultOverview: {
    title: 'Result Overview',
    fullPath: '/company/:company/project/:project/result-overview',
    pattern: {
      path: 'result-overview',
    },
    analyticsModuleName: ANALYTICS_MODULE_NAMES.RESULTS,
  },
  statistics: {
    title: 'Statistics',
    fullPath: '/company/:company/project/:project/statistics',
    pattern: {
      path: 'statistics',
    },
  },
  timeTracker: {
    title: 'Time Tracker',
    fullPath: '/company/:company/project/:project/time-tracker',
    pattern: {
      path: 'time-tracker',
    },
    analyticsModuleName: ANALYTICS_MODULE_NAMES.TIME_TRACKER,
  },
  costTracker: {
    title: i18n.t(translations.costTracker_costTracker),
    fullPath: 'company/:company/project/:project/cost-tracker',
    pattern: {
      path: 'cost-tracker',
    },
    analyticsModuleName: ANALYTICS_MODULE_NAMES.COST_TRACKER,
  },
  sentryTest: {
    title: 'Sentry test',
    fullPath: '/company/:company/test-sentry',
    pattern: {
      path: 'company/:company/test-sentry',
    },
  },
  userSettings: {
    title: 'User settings',
    fullPath: '/company/:company/user-settings/*',
    pattern: {
      path: '/company/:company/user-settings/*',
    },
    nested: {
      templates: {
        title: 'Templates',
        fullPath: '/company/:company/user-settings/templates',
        pattern: {
          path: 'templates',
        },
        analyticsModuleName: ANALYTICS_MODULE_NAMES.AUTOMATION_RULESETS,
      },
      kpi: {
        title: 'KPI',
        fullPath: '/company/:company/user-settings/kpi',
        pattern: {
          path: 'kpi',
        },
      },
      featureFlags: {
        title: 'Feature flags',
        fullPath: '/company/:company/user-settings/feature-flags',
        pattern: {
          path: 'feature-flags',
        },
      },
    },
  },
  companySettings: {
    title: 'Company settings',
    fullPath: '/company/:company/company-settings/*',
    pattern: {
      path: '/company/:company/company-settings/*',
    },
    nested: {
      currency: {
        title: 'Currency',
        fullPath: '/company/:company/company-settings/currency',
        pattern: {
          path: 'currency',
        },
      },
      hierarchy: {
        title: 'Hierarchy',
        fullPath: '/company/:company/company-settings/hierarchy',
        pattern: {
          path: 'hierarchy/*',
        },
      },
      hierarchyPermissions: {
        title: 'Hierarchy Permissions',
        fullPath: '/company/:company/company-settings/hierarchy/hierarchy-permissions',
        pattern: {
          path: 'hierarchy/hierarchy-permissions',
        },
      },
      hierarchyOptionalParameters: {
        title: 'Hierarchy Parameters',
        fullPath: '/company/:company/company-settings/hierarchy/hierarchy-optional-parameters',
        pattern: {
          path: 'hierarchy/hierarchy-optional-parameters',
        },
      },
      operationCodes: {
        title: 'Operation codes',
        fullPath: '/company/:company/company-settings/operation-codes',
        pattern: {
          path: 'operation-codes',
        },
      },
      templates: {
        title: 'Templates',
        fullPath: '/company/:company/company-settings/templates',
        pattern: {
          path: 'templates',
        },
      },
      kpi: {
        title: 'KPI',
        fullPath: '/company/:company/company-settings/kpi',
        pattern: {
          path: 'kpi',
        },
      },
      timeTracker: {
        title: 'Time Tracker',
        fullPath: '/company/:company/company-settings/time-tracker',
        pattern: {
          path: 'time-tracker',
        },
      },
      sectionsDataStructure: {
        title: 'Data Structure',
        fullPath: '/company/:company/company-settings/sections-data-structure',
        pattern: {
          path: 'sections-data-structure',
        },
      },
      priceBook: {
        title: 'Price Book',
        fullPath: '/company/:company/company-settings/price-book',
        pattern: {
          path: 'price-book',
        },
      },
      rigOperationTemplates: {
        title: 'Rig Operation Templates',
        fullPath: '/company/:company/company-settings/rig-operation-templates',
        pattern: {
          path: 'rig-operation-templates',
        },
      },
      costCodes: {
        title: 'Cost Codes',
        fullPath: '/company/:company/company-settings/cost-codes',
        pattern: {
          path: 'cost-codes',
        },
      },
      costSetup: {
        title: 'Cost Setup',
        fullPath: '/company/:company/company-settings/cost-setup',
        pattern: {
          path: 'cost-setup',
        },
      },
    },
  },
  compare: {
    title: 'Compare',
    fullPath: '/company/:company/project/:project?/compare',
    pattern: {
      path: '/company/:company/project/:project?/compare',
    },
    analyticsModuleName: ANALYTICS_MODULE_NAMES.COMPARISONS,
  },
  campaigns: {
    title: 'Campaigns',
    fullPath: '/company/:company/project/:project?/campaigns',
    pattern: {
      path: '/company/:company/project/:project?/campaigns',
    },
    analyticsModuleName: ANALYTICS_MODULE_NAMES.CAMPAIGNS,
  },
  notFound: {
    title: 'Not Found',
    fullPath: '*',
    pattern: {
      path: '*',
    },
  },
});
